// client/src/components/auth/VerifyEmail.js
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import toastr from 'toastr'
import useAuth from '../../hooks/useAuth'
import flex from '../../api/FlexFetch'
import './Auth.css'

function SmallIcon({ name, className = '' }) {
  return (
    <div className={`verify-email-icon mb-3 ${className}`} aria-hidden>
      <i className={`bi ${name}`} style={{ fontSize: '3rem' }}></i>
    </div>
  )
}

function VerifyEmail() {
  const navigate = useNavigate()
  const { refreshUser } = useAuth()
  const [searchParams] = useSearchParams()
  const [verifying, setVerifying] = useState(true)
  const [verified, setVerified] = useState(false)
  const [error, setError] = useState(null)
  const [countdown, setCountdown] = useState(3)

  const token = searchParams.get('token')

  useEffect(() => {
    if (!token) {
      setError('No verification token provided')
      setVerifying(false)
      return
    }

    let mounted = true

    const handleEmailVerification = async (verificationToken) => {
      try {
        const response = await flex.flexFetch('/api/1.0/auth/verify-email', {
          method: 'POST',
          body: JSON.stringify({ token: verificationToken }),
        })

        const data = await response.json()

        if (!mounted) return

        if (response.ok) {
          toastr.success('Email verified successfully!')
          setVerified(true)
          setVerifying(false)
          setCountdown(3)

          // Refresh auth context to pick up the new session
          try {
            await refreshUser()
          } catch (err) {
            console.error('Failed to refresh user after verification:', err)
          }
        } else {
          setVerified(false)
          const isExpired = data?.error?.details?.expired || data?.expired
          if (isExpired) {
            setError(
              'This verification link has expired. You can request a new one.',
            )
          } else {
            const errMsg =
              data?.error?.message ||
              (typeof data?.error === 'string' ? data.error : null) ||
              'Failed to verify email. Please try again.'
            setError(errMsg)
          }
          const toastrMsg =
            data?.error?.message ||
            (typeof data?.error === 'string' ? data.error : null) ||
            'Failed to verify email'
          toastr.error(toastrMsg)
        }
      } catch (err) {
        console.error('Email verification error:', err)
        if (!mounted) return
        setVerified(false)
        setError('Network error occurred. Please try again.')
        toastr.error('Network error. Please try again.')
      } finally {
        if (mounted) setVerifying(false)
      }
    }

    handleEmailVerification(token)

    return () => {
      mounted = false
    }
  }, [token, refreshUser])

  useEffect(() => {
    if (!verifying && error == null && countdown > 0) {
      const t = setTimeout(() => setCountdown((c) => c - 1), 1000)
      return () => clearTimeout(t)
    }
    if (countdown === 0 && error == null && !verifying) {
      // Redirect to root (RootRoute will send to landing or dashboard)
      navigate('/')
    }
  }, [verifying, countdown, error, navigate])

  const goToResendPage = () => navigate('/verify-email-pending')
  const goToSignIn = () => navigate('/sign-in')

  return (
    <div className="auth-container">
      <div className="auth-card auth-form">
        <div className="text-center">
          {verifying ? (
            <>
              <h2 className="auth-title">Verifying your email</h2>
              <SmallIcon name="bi-hourglass-split text-primary" />
              <div
                className="spinner-border text-primary"
                role="status"
                aria-hidden
              >
                <span className="visually-hidden">Verifying...</span>
              </div>
              <p className="mt-3 text-muted">
                Please wait while we verify your email address.
              </p>
            </>
          ) : verified ? (
            <>
              <h2 className="auth-title">Email Verified</h2>
              <SmallIcon name="bi-check-circle text-success" />
              <p className="text-success">
                <span data-testid="verify-email-success">
                  Your email has been verified successfully.
                </span>
              </p>
              <p className="text-muted">
                Redirecting you in {countdown} second
                {countdown !== 1 ? 's' : ''}...
              </p>
              <div className="d-grid gap-2">
                <button
                  className="btn btn-primary"
                  data-testid="verify-email-go-now"
                  onClick={() => navigate('/')}
                >
                  Go now
                </button>
                <button className="btn btn-link" onClick={goToSignIn}>
                  Go to Sign In
                </button>
              </div>
            </>
          ) : error ? (
            <>
              <h2 className="auth-title">Verification Failed</h2>
              <SmallIcon name="bi-exclamation-triangle text-danger" />
              <div className="alert alert-danger" role="alert">
                <span data-testid="verify-email-error">{error}</span>
              </div>
              <div className="d-grid gap-2">
                <button className="btn btn-primary" onClick={goToResendPage}>
                  Request New Verification Email
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={goToSignIn}
                >
                  Go to Sign In
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default VerifyEmail
